import { Component, Mixins } from 'vue-property-decorator'
import IconsMixin from '@/mixins/IconsMixin.vue'
import AuthMixin from '@/mixins/AuthMixin.vue'
import LabeledValue from '@/components/LabeledValue.vue'
import {
  CompanyTypeEnum,
  IOrder,
  Order,
  OrderCollection,
  OrderEntry,
  OrderMapper,
  ReminderCollection
} from '@/firestore'
import LoadingPage from '@/components/LoadingPage.vue'
import axios from 'axios'
import OrderListItem from '@/components/Order/OrderListItem.vue'
import store, { Actions } from '../../../store'

interface LicenseInfo {
  filename: string,
  mimeType: string,
  size: string,
  createdAt: string
}

@Component({
  components: {OrderListItem, LoadingPage, LabeledValue}
})
export default class OrderDetail extends Mixins(IconsMixin, AuthMixin) {
  public order: Order | null = null
  public selectedEntryIndex: number = -1
  public orderedByCompanyLocation: string = ''
  public hasLicense = false
  public hasMounted = false
  public licenseInfo: LicenseInfo | false | null = null
  public reminders: Map<string, string> = new Map();
  // button loading actions
  public isShipping = false
  public isSavingEntry = false
  public isFindingLicense = false
  public isDownloading = false

  public get selectedEntry() {
    if (!this.order) {
      return null
    }
    return this.order.orderEntries[this.selectedEntryIndex]
  }

  public lookUpReminder(selectedEntry: OrderEntry) {
    if (!this.order) {
      return null
    }
    let reminderReturn = this.reminders.get(selectedEntry.id + selectedEntry.vendorCompanyId)
    if (!reminderReturn)
      reminderReturn = "None"
    return reminderReturn;
  }

  private async setReminders(order: any) {
    order.orderEntries.forEach(async (entry: any) => {
      if (!entry.priceListEntryId || !entry.vendorCompanyId) {
        return
      }

      let reminder = await this.searchReminders(entry.priceListEntryId, entry.vendorCompanyId)
      if (reminder) {
        this.reminders.set(entry.id + entry.vendorCompanyId, reminder.text);
      }
    });
  };

  public async mounted() {
    try {
      const collection = new OrderCollection((await this.$firebase).firestore())

      this.order = await collection.get(this.$route.params.id)
      if (!this.order) {
        const err = new Error('You do not have permissions to view that page')
        return store.dispatch(Actions.SET_ERROR, [err, this.$router])
      }

      if (!this.order.readReceipt && this.userCompany.type === CompanyTypeEnum.VENDOR) {
        await this.setReadReceipt()
      }
      await this.setReminders(this.order)
      if (this.$route.query.action === 'approve') {
        this.shipOrder()
      }

      this.orderedByCompanyLocation = (await axios.get('/api/restaurants', {
        params: {orderedByCompanyId: this.order.orderedByCompanyId}
      })).data

      try {
        this.isFindingLicense = true

        this.licenseInfo = (await axios.get(
          `/api/restaurants/${this.order.orderedByCompanyId}/liquor-license/info`, {
            params: {userCompanyId: this.userCompany.id}
          }
        )).data

        this.hasLicense = true
        this.isFindingLicense = false
      } catch (err) {
        this.hasLicense = false
        this.isFindingLicense = false
      }

      this.hasMounted = true
    } catch (error) {
      const err = new Error('You do not have permissions to view that page')
      return store.dispatch(Actions.SET_ERROR, [err, this.$router])
    }
  }

  public async editOrder() {
    if (!this.order || !this.order.canEdit) {
      return
    }

    await this.$router.push({name: 'vendor-order-edit', params: {id: this.order.id}})
  }

  public async shipOrder() {
    if (!this.order || !this.order.canShip) {
      return
    }

    this.isShipping = true
    const orderData = (await axios.post(`/api/order/${this.order.id}/ship`)).data
    this.order = (new OrderMapper()).map(orderData as IOrder)
    this.isShipping = false
  }

  public async saveOrderEntry(entry: OrderEntry) {
    if (!this.order || !this.order.canShip) {
      return
    }

    this.isSavingEntry = true
    await this.saveOrder()
    this.isSavingEntry = false
  }

  public async download() {
    if (!this.licenseInfo) {
      return
    }

    this.isDownloading = true

    if (!this.order || !this.order.orderedByCompanyId) {
      return console.error('Missing orderedByCompanyId')
    }

    const res = await axios.get(`/api/restaurants/${this.order.orderedByCompanyId}/liquor-license`, {
      responseType: 'blob',
      params: {userCompanyId: this.userCompany.id}
    })
    const file = new File(
      [res.data as Blob],
      this.licenseInfo.filename,
      {
        type: res.headers['content-type']
      }
    )

    this.isDownloading = false
    this.promptDownload(file)
  }

  private promptDownload(file: File) {
    const url = URL.createObjectURL(file)
    const anchor = document.createElement('a')
    anchor.href = url
    anchor.download = file.name

    anchor.addEventListener('click', () => {
      setTimeout(() => {
        URL.revokeObjectURL(url)
        anchor.remove()
      }, 0)
    }, false)
    anchor.click()
  }

  protected async setReadReceipt() {
    if (!this.order) {
      return
    }
    const collection = new OrderCollection((await this.$firebase).firestore())

    this.order.readReceipt = {
      userId: this.userClaim.user_id,
      date: new Date()
    }

    return collection.save(this.order)
  }

  protected async saveOrder() {
    if (!this.order) {
      return
    }

    const collection = new OrderCollection((await this.$firebase).firestore())
    await collection.save(this.order)
  }

  private async searchReminders(orderEntryId: string, vendorCompanyId: string) {
    const remindersCollection = new ReminderCollection((await this.$firebase).firestore());
    return await remindersCollection.listRemindersByPriceListEntryIdAndVendorCompanyId(orderEntryId, vendorCompanyId)
  }
}
